import React, {useMemo} from 'react';
import {toast} from 'react-toastify';
import {Loader} from 'semantic-ui-react';

import {
  DefaultResourceConfig,
  useAllProjectsQuery,
  useEntityDefaultResourceConfigsQuery,
} from '../../../../generated/graphql';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../../../../pages/EntityLaunchPage';
import {useEntityQueueOptions, useProjectView} from '../../hooks';
import {useJobDetailsOptions} from '../hooks';
import {OptionType} from '../utils';
import {CreateTriggerDrawerLoaded} from './CreateTriggerDrawerLoaded';

type CreateTriggerDrawerInnerProps = {
  entityName: string;
  projectName: string;
  projectID: string;
  artifactCollectionID?: string;
  onClose: () => void;
  refetchProjectTriggers: () => Promise<any>;
  refetchCollectionTriggers?: () => Promise<any>;
  customTriggerOptions?: OptionType[];
};

export const CreateTriggerDrawerInner = ({
  entityName,
  projectName,
  projectID,
  artifactCollectionID,
  onClose,
  refetchProjectTriggers,
  refetchCollectionTriggers,
  customTriggerOptions,
}: CreateTriggerDrawerInnerProps) => {
  const queryAllProjects = useAllProjectsQuery({
    variables: {entityName, first: 1000, order: '-createdAt'},
    fetchPolicy: 'cache-and-network',
  });
  // Whether project scoped or entity scoped (via the model registry), all triggers are set
  // under a project. Hence, run colors can be derived from the trigger project.
  const queryColors = useProjectView(entityName, projectName);

  const queryResourceConfigs = useEntityDefaultResourceConfigsQuery({
    variables: {entityName},
  });
  const defaultResourceConfigs = useMemo(() => {
    const mapIdToResource: Record<string, DefaultResourceConfig> = {};
    const edges =
      queryResourceConfigs.data?.entity?.defaultResourceConfigs?.edges ?? [];
    for (const edge of edges) {
      mapIdToResource[edge.node.id] = edge.node;
    }
    return mapIdToResource;
  }, [queryResourceConfigs]);
  const queryQueues = useEntityQueueOptions(
    entityName,
    projectName,
    defaultResourceConfigs,
    true
  );
  const {
    data: jobDetailsMap,
    loading,
    error,
  } = useJobDetailsOptions(entityName, projectName);

  const allProjects =
    queryAllProjects.data?.projects?.edges
      .map(p => p.node?.name ?? '')
      .filter(p => p !== '' && p !== DEFAULT_LAUNCH_PROJECT_NAME) ?? [];

  if (
    error ||
    queryColors.error ||
    queryQueues.error ||
    queryResourceConfigs.error
  ) {
    onClose();
    toast.error(`Error fetching project jobs' information`);
    return null;
  }

  if (
    queryAllProjects.loading ||
    loading ||
    !jobDetailsMap ||
    queryColors.loading ||
    queryQueues.loading ||
    queryResourceConfigs.loading
  ) {
    return <Loader active name="create-trigger-drawer-projects" />;
  }
  return (
    <CreateTriggerDrawerLoaded
      entityName={entityName}
      projectName={projectName}
      projectID={projectID}
      artifactCollectionID={artifactCollectionID}
      allProjects={allProjects}
      queues={queryQueues.queues}
      jobDetailsMap={jobDetailsMap}
      onClose={onClose}
      refetchProjectTriggers={refetchProjectTriggers}
      refetchCollectionTriggers={refetchCollectionTriggers}
      customTriggerOptions={customTriggerOptions}
      customRunColors={queryColors?.customRunColors}
    />
  );
};
